import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  addSignInMethodStartResponseSchema,
  availableSessionSchema,
  groupSchema,
  DEFAULT_TIMEZONE,
  joinOffersResponseSchema,
  joinResponseSchema,
  meResponseSchema,
  memberHomeSchema,
  bookAgainResponseSchema,
  type BookAgainResponse,
  myBusinessSchema,
  participantSchema,
  scheduleGroupSchema,
  signInUserSchema,
  type AddSignInMethodStartResponse,
  type AvailableSession,
  type Group,
  type CreateParticipantRequest,
  type JoinOffer,
  type JoinResponse,
  type MeResponse,
  type MemberHome,
  type MyBusiness,
  type Participant,
  type PhoneSignInChannel,
  type ScheduleGroup,
  type SignInMethodKind,
  type SignInUser,
  type UpdateMeRequest,
  type UpdateParticipantRequest,
} from "@teamna/shared";
import { useEffect, useMemo } from "react";
import { api } from "../api/client.ts";
import { daysAgoIso, inDaysIso, startOfTodayIso } from "../lib/format.ts";
import {
  setActiveBusinessId,
  useActiveBusinessId,
} from "../lib/active-business.ts";
import { queryKeys } from "./keys.ts";
import { identifyUser } from "../analytics/web.ts";

/**
 * The member data layer.
 *
 * One hook per endpoint, named after it, so a screen never builds a path or a query key by hand.
 * Mirrors `apps/mobile/src/hooks/use-member.ts`; add owner hooks in a sibling `use-owner.ts`
 * rather than growing this file.
 */

/** GET /v1/me */
export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: queryKeys.me,
    // The account is known here first after a sign-in, so analytics learns who it is here too.
    queryFn: async () => {
      const me = await api.get("/me", meResponseSchema);
      identifyUser(me.user.id);
      return me;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * PATCH /v1/me
 *
 * Writes the fresh record straight into the cache rather than invalidating, because the account
 * holder's name is on screen when it is edited and a round trip to re-read what was just sent back
 * would flash the old value.
 */
export function useUpdateMe(): UseMutationResult<MeResponse, unknown, UpdateMeRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeRequest) => api.patch("/me", input, meResponseSchema),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.me, data);
    },
  });
}

/**
 * GET /v1/me/businesses
 *
 * Read again every 5 minutes while the tab is open, so the end of a pilot (`pilotReplay`) reaches an
 * open Desk in minutes. Desk `main.tsx` gives each new list to `syncPlaceReplay`.
 */
/** The query of `GET /v1/me/businesses`, shared by the hook and by a write that must read it now. */
const businessesQuery = {
  queryKey: queryKeys.businesses,
  queryFn: () => api.list("/me/businesses", myBusinessSchema),
};

export function useBusinesses(): UseQueryResult<MyBusiness[]> {
  return useQuery({
    ...businessesQuery,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * GET /v1/me/join-offers: the businesses that invited the account, added its family, or ended its
 * membership while the family is still there. The landing asks only when the account has no place.
 */
export function useJoinOffers(options: { enabled?: boolean } = {}): UseQueryResult<JoinOffer[]> {
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;
  // Each screen that reads the offers starts its own read: a request from before the screen opened
  // can hold a list from before an invitation (review F20.1).
  useEffect(() => {
    if (!enabled) return;
    const key = { queryKey: queryKeys.joinOffers, exact: true };
    void queryClient.cancelQueries(key).then(() => queryClient.refetchQueries(key));
  }, [enabled, queryClient]);
  return useQuery({
    queryKey: queryKeys.joinOffers,
    // The business list is read again after the offers, not with them: a place made on another
    // device (a join, an approval) must count before a screen decides from the offers, with or
    // without one (review F12.1, F13.1, F14.1). A read of the list that started before is cancelled,
    // as after a take. When that read fails, the offers that came stay (the cards show, and the
    // server checks each take again; review F15.1); with no offer, the read fails, so no screen
    // decides "nothing waits" on an old list.
    queryFn: async ({ signal }) => {
      const { offers } = await api.get("/me/join-offers", joinOffersResponseSchema);
      // A read that was cancelled (a newer one started) stops here: it must not cancel the list
      // read of the newer one (review F21.1).
      if (signal.aborted) return offers;
      await queryClient.cancelQueries({ queryKey: businessesQuery.queryKey, exact: true });
      try {
        await queryClient.fetchQuery({ ...businessesQuery, staleTime: 0 });
      } catch (error) {
        if (offers.length === 0) throw error;
      }
      return offers;
    },
    enabled,
    // Read again on each visit: a business can invite or add the person at any moment, and a list
    // from a minute ago would send them to the owner sign-up (review F9.1). The screens wait for it.
    staleTime: 0,
  });
}

/**
 * POST /v1/me/join-offers/:businessId. It reads the business list again before the caller moves
 * on, so the landing reads the new place.
 */
export function useAcceptJoinOffer(): UseMutationResult<JoinResponse, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (businessId: string) => api.post(`/me/join-offers/${businessId}`, undefined, joinResponseSchema),
    // The landing reads the business list, so the new place must be in it before the screen moves
    // on. `fetchQuery` throws when the read fails (an invalidation would not, and the old empty list
    // would open the owner sign-up; review F2.3): the mutation then fails, the offer card stays, and
    // another tap answers `alreadyJoined` and reads again. The offers go last, after the list.
    onSuccess: async (result) => {
      setActiveBusinessId(result.businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
      // A read of the list that started before the join can still answer the old list: cancel it,
      // so `fetchQuery` starts a new read after the commit (review F3.2).
      await queryClient.cancelQueries({ queryKey: businessesQuery.queryKey, exact: true });
      await queryClient.fetchQuery({ ...businessesQuery, staleTime: 0 });
      await queryClient.invalidateQueries({ queryKey: queryKeys.joinOffers });
    },
  });
}

/** GET /v1/me/participants */
export function useParticipants(): UseQueryResult<Participant[]> {
  return useQuery({
    queryKey: queryKeys.participants,
    queryFn: () => api.list("/me/participants", participantSchema),
    staleTime: 5 * 60 * 1000,
  });
}

/** POST /v1/me/participants */
export function useCreateParticipant(): UseMutationResult<
  Participant,
  unknown,
  CreateParticipantRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateParticipantRequest) =>
      api.post("/me/participants", input, participantSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      // The home lists the participants, and opens on "Add your participants" when there are none.
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/**
 * PATCH /v1/me/participants/:id
 *
 * The schedule is grouped by participant and carries the display name in each group, so a rename
 * has to reach it too or the agenda keeps showing the old one until the window rolls over.
 */
export function useUpdateParticipant(): UseMutationResult<
  Participant,
  unknown,
  UpdateParticipantRequest & { id: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateParticipantRequest & { id: string }) =>
      api.patch(`/me/participants/${id}`, input, participantSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/** DELETE /v1/me/participants/:id */
export function useDeleteParticipant(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/me/participants/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/**
 * POST /v1/join/:code
 *
 * Codes are read out over the phone during a pilot, so they are upper-cased and trimmed here
 * rather than being rejected for the shape they arrived in.
 */
export function useJoinBusiness(): UseMutationResult<JoinResponse, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post(
        `/join/${encodeURIComponent(code.trim().toUpperCase())}`,
        undefined,
        joinResponseSchema,
      ),
    // Wait for the fresh businesses list before the caller moves on. The landing rule reads this
    // cache, and a stale empty list sends a person who just joined to the first-day fork. No screen
    // watches the list at this moment, so the default refetch of active queries would not wait.
    onSuccess: async (result) => {
      setActiveBusinessId(result.businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
      await queryClient.invalidateQueries({ queryKey: queryKeys.businesses, refetchType: "all" });
    },
  });
}

/**
 * How far back the agenda remembers, and how far ahead it looks.
 *
 * Forward is the session generation horizon from TRD section 5, so the app never shows a week the
 * generator has not materialised yet. Backward is a term's worth: long enough to answer "did Zayd
 * have swimming last month", short enough that a member of two years is not downloading their
 * whole history to open the Schedule tab.
 */
export const SCHEDULE_PAST_DAYS = 90;
export const SCHEDULE_AHEAD_DAYS = 56;

/**
 * GET /v1/me/schedule?from&to
 *
 * Bookings grouped by participant, spanning both directions in one request.
 *
 * One window rather than one per tab, because the Schedule screen's Upcoming and Past are a
 * partition of the same rows: fetching them separately would mean two query keys, two loading
 * states, and a booking detail screen that can open a row from one tab and not the other. The
 * split is done in the screen, on data it already has.
 */
export function useSchedule(options?: { from?: string; to?: string }): UseQueryResult<ScheduleGroup[]> {
  const from = options?.from ?? daysAgoIso(SCHEDULE_PAST_DAYS);
  const to = options?.to ?? inDaysIso(SCHEDULE_AHEAD_DAYS);

  return useQuery({
    queryKey: queryKeys.schedule(from, to),
    queryFn: () => api.list("/me/schedule", scheduleGroupSchema, { query: { from, to } }),
    staleTime: 60 * 1000,
  });
}

/**
 * GET /v1/businesses/:id/sessions?from&to&groupId
 *
 * Open availability, with `spotsLeft` and `bookable` already worked out by the server.
 *
 * Fixed weekly slots never appear here, by design: the business places a participant in one, so
 * it is not something a member books or moves into (decision D12). That is why the Book tab at a
 * swimming academy can be legitimately empty while the same member has a full schedule.
 */
export function useAvailableSessions(params: {
  businessId: string | null;
  from?: string;
  to?: string;
  groupId?: string | null;
  /** The person the booking is for: a child sees only the sessions of their own group. */
  participantId?: string | null;
}): UseQueryResult<AvailableSession[]> {
  const from = params.from ?? startOfTodayIso();
  const to = params.to ?? inDaysIso(30);
  const groupId = params.groupId ?? null;
  const participantId = params.participantId ?? null;

  return useQuery({
    queryKey: queryKeys.sessions(params.businessId ?? "none", from, to, groupId, participantId),
    queryFn: () =>
      api.list(`/businesses/${params.businessId}/sessions`, availableSessionSchema, {
        query: { from, to, groupId: groupId ?? undefined, participantId: participantId ?? undefined },
      }),
    enabled: Boolean(params.businessId),
    staleTime: 30 * 1000,
  });
}

/**
 * GET /v1/me/businesses/:id/home
 *
 * The member home of one business: each participant's group and colour, usual days, sessions left
 * (in total and for their own group, with the packages that can pay), the venue and any payment
 * due. Answers MEMBERSHIP_NOT_ACTIVE while the business has not approved the member yet.
 */
export function useMemberHome(businessId: string | null): UseQueryResult<MemberHome> {
  return useQuery({
    queryKey: queryKeys.memberHome(businessId ?? "none"),
    queryFn: () => api.get(`/me/businesses/${businessId}/home`, memberHomeSchema),
    enabled: Boolean(businessId),
    staleTime: 15 * 1000,
  });
}

/**
 * POST /v1/me/businesses/:id/membership/cancel (M2): the member's membership does not renew. It
 * ends with the paid month. The home that comes back shows it.
 */
export function useCancelMyMembership(businessId: string | null): UseMutationResult<MemberHome, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/me/businesses/${businessId}/membership/cancel`, {}, memberHomeSchema),
    onSuccess: (home) => {
      queryClient.setQueryData(queryKeys.memberHome(businessId ?? "none"), home);
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHome(businessId ?? "none") });
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentRequests });
    },
  });
}

/**
 * GET /v1/me/businesses/:id/book-again: the next free times of the client's usual classes (B1).
 * Only where clients book for themselves: pass null elsewhere.
 */
export function useBookAgain(businessId: string | null): UseQueryResult<BookAgainResponse> {
  return useQuery({
    queryKey: queryKeys.bookAgain(businessId ?? "none"),
    queryFn: () => api.get(`/me/businesses/${businessId}/book-again`, bookAgainResponseSchema),
    enabled: Boolean(businessId),
    staleTime: 15 * 1000,
  });
}

/** GET /v1/businesses/:id/groups */
export function useGroups(businessId: string | null): UseQueryResult<Group[]> {
  return useQuery({
    queryKey: queryKeys.groups(businessId ?? "none"),
    queryFn: () => api.list(`/businesses/${businessId}/groups`, groupSchema),
    enabled: Boolean(businessId),
    staleTime: 10 * 60 * 1000,
  });
}

export type ActiveBusiness = {
  business: MyBusiness | null;
  businesses: MyBusiness[];
  isLoading: boolean;
  error: unknown;
  setActiveBusinessId: (id: string | null) => void;
};

/**
 * The business the member is currently looking at.
 *
 * Falls back to the first one whenever nothing is stored or the stored id is not in the list any
 * more, so a stale choice from a business the member left can never strand them on an empty Book tab.
 * The fallback is then written back, so the switcher and the screen agree about what is selected.
 */
export function useActiveBusiness(): ActiveBusiness {
  const query = useBusinesses();
  const storedId = useActiveBusinessId();
  const businesses = useMemo(() => query.data ?? [], [query.data]);

  const business = useMemo(
    () => businesses.find((row) => row.businessId === storedId) ?? businesses[0] ?? null,
    [businesses, storedId],
  );

  useEffect(() => {
    if (business && business.businessId !== storedId) setActiveBusinessId(business.businessId);
  }, [business, storedId]);

  return {
    business,
    businesses,
    isLoading: query.isLoading,
    error: query.error,
    setActiveBusinessId,
  };
}

/**
 * Business id to timezone, for rendering a session in the business's wall clock.
 *
 * A schedule entry carries the business name but not its timezone, and a member can have participants
 * at two businesses, so the lookup has to be per booking rather than one setting for the screen.
 */
export function useBusinessTimezones(): (businessId: string) => string {
  const businesses = useBusinesses();
  return useMemo(() => {
    const byId = new Map((businesses.data ?? []).map((row) => [row.businessId, row.timezone]));
    return (businessId: string) => byId.get(businessId) ?? DEFAULT_TIMEZONE;
  }, [businesses.data]);
}

/** POST /v1/me/sign-in-methods/start */
export function useStartSignInMethod(): UseMutationResult<
  AddSignInMethodStartResponse,
  unknown,
  { identifier: string; channel?: PhoneSignInChannel }
> {
  return useMutation({
    mutationFn: (input) => api.post("/me/sign-in-methods/start", input, addSignInMethodStartResponseSchema),
  });
}

/** POST /v1/me/sign-in-methods/verify. The profile re-reads `/me`, which carries the methods. */
export function useVerifySignInMethod(): UseMutationResult<SignInUser, unknown, { identifier: string; code: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post("/me/sign-in-methods/verify", input, signInUserSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

/** DELETE /v1/me/sign-in-methods/{kind} */
export function useRemoveSignInMethod(): UseMutationResult<void, unknown, SignInMethodKind> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind) => api.delete(`/me/sign-in-methods/${kind}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
}
